import { useState } from 'react';
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { CodeBlock, MetaRow } from './CodeBlock';
import {
  commandArgsOf,
  formatArgValue,
  procedureRequestOf,
} from './traceDetail';
import type { Span, SpanActor } from './types';

/**
 * The readable sections of the trace inspector's Result tab: what the agent was told (a tool
 * call's instruction, the procedure it named and the parameters it passed), what an action did
 * (its exact arguments), and the model's thinking. Everything here was previously reachable only
 * as raw JSON under Input/Output, or not at all.
 */

/** The small uppercase label every section opens with. */
export function SectionHeading({ children }: { children: string }) {
  return (
    <p className="mb-1.5 text-[10px] font-semibold tracking-widest uppercase text-stone-500">
      {children}
    </p>
  );
}

const ACTOR_CLASS: Record<SpanActor, string> = {
  procedure: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  healer: 'border-amber-200 bg-amber-50 text-amber-800',
};

/** "Procedure" / "Healer" — who performed a row that was not the main agent's own tool call. */
export function ActorBadge({ actor }: { actor: SpanActor }) {
  const gt = useGT();
  const label =
    actor === 'healer'
      ? gt('Healer', {
          context:
            'agent trace badge: an action the procedure healer improvised after a step drifted',
        })
      : gt('Procedure', {
          context:
            'agent trace badge: an action performed by a replayed procedure (SOP)',
        });
  return (
    <span
      className={`shrink-0 rounded border px-1 py-px text-[9.5px] font-semibold uppercase tracking-wide ${ACTOR_CLASS[actor]}`}
    >
      {label}
    </span>
  );
}

/** A key/value list, one row per entry. */
function KeyValueList({ entries }: { entries: Array<[string, string]> }) {
  return (
    <div className="rounded-lg border border-stone-200 px-2.5 py-1">
      {entries.map(([key, value]) => (
        <MetaRow key={key} label={key} value={value} mono />
      ))}
    </div>
  );
}

/**
 * What the agent handed this call: the operator instruction, the named procedure and every
 * parameter it passed. Renders nothing for a call that carried none of them.
 */
export function ProcedureRequestSection({ span }: { span: Span }) {
  const gt = useGT();
  const request = procedureRequestOf(span.toolInput);
  if (!request) return null;
  return (
    <div className="flex flex-col gap-2">
      {request.instruction && (
        <div>
          <SectionHeading>
            {gt('Instructions', {
              context:
                'agent trace detail section: the instruction the main agent gave this tool call or procedure',
            })}
          </SectionHeading>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-stone-800">
            {request.instruction}
          </p>
        </div>
      )}
      {request.procedureName && (
        <MetaRow
          label={gt('Procedure', {
            context:
              'agent trace detail field: the saved procedure the main agent asked to run',
          })}
          value={request.procedureName}
        />
      )}
      {request.parameters.length > 0 && (
        <div>
          <SectionHeading>
            {gt('Parameters passed', {
              context:
                'agent trace detail section: parameter values the main agent passed to a procedure',
            })}
          </SectionHeading>
          <KeyValueList entries={request.parameters} />
        </div>
      )}
    </div>
  );
}

/**
 * The action's exact command: its name and every argument (the key pressed, the text typed, the
 * point clicked). A canonical action row carries its arguments as its own input; an audit row
 * carries them nested under `actionArgs`.
 */
export function ActionCommandSection({ span }: { span: Span }) {
  const gt = useGT();
  const args = commandArgsOf(span);
  if (!args) return null;
  const entries = Object.entries(args).map(([key, value]): [string, string] => [
    key,
    formatArgValue(value),
  ]);
  return (
    <div>
      <SectionHeading>
        {gt('What it did', {
          context:
            'agent trace detail section: the exact action and arguments (what was clicked or typed)',
        })}
      </SectionHeading>
      {span.action && (
        <p className="mb-1 font-mono text-[12px] text-stone-800">
          {span.action}
        </p>
      )}
      <KeyValueList entries={entries} />
    </div>
  );
}

/** The model's extended thinking, collapsed by default: long, and the model talking to itself. */
export function ThinkingSection({
  thinking,
}: {
  thinking: string | null | undefined;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  if (!thinking) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1 rounded-md px-1 py-1 text-left text-[11px] font-semibold uppercase tracking-widest text-stone-500 hover:bg-stone-100"
        >
          {open ? (
            <CaretDownIcon className="h-3 w-3 shrink-0" />
          ) : (
            <CaretRightIcon className="h-3 w-3 shrink-0" />
          )}
          {gt('Model thinking', {
            context:
              'agent trace detail collapsible: the model’s extended thinking before this action',
          })}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">
        <CodeBlock maxHeight={320}>{thinking}</CodeBlock>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A replayed step's authored instruction and, on a branch node, the path it took. */
export function StepInstructionSection({ span }: { span: Span }) {
  const gt = useGT();
  if (!span.sopStepDescription && !span.sopBranch) return null;
  return (
    <div className="flex flex-col gap-2">
      {span.sopStepDescription && (
        <div>
          <SectionHeading>
            {gt('Step instruction', {
              context:
                'agent trace detail section: the authored instruction of a replayed procedure step',
            })}
          </SectionHeading>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-stone-800">
            {span.sopStepDescription}
          </p>
        </div>
      )}
      {span.sopBranch && (
        <MetaRow
          label={gt('Path taken', {
            context:
              'agent trace detail field: which labelled path a procedure branch step chose',
          })}
          value={`${span.sopBranch.label} (${span.sopBranch.mode})`}
        />
      )}
    </div>
  );
}
